import React, { useMemo } from 'react';
import { TreeTaskCard } from 'shared/components/connected/task-card';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { ID, IDPrefixes } from 'shared/types/id';
import { untaggedId } from 'shared/types/orderable-section';
import { RelationshipUpdate } from 'shared/types/relationship';
import { Task } from 'shared/types/task';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from 'shared/utils/prefix-id';

import { useTagTree } from '../../hooks/use-tag-tree';
import { GroupedListProps } from '../../types';
import { TagHeader } from '../tag-header';

const taskTreeItemTypes = [TreeItemTypes.Task];

export const TagGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  tags,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
}) => {
  const today = useToday();
  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useTagTree({
    tasks,
    tags,
    relationships,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
  });

  const oldTreeData = useMemo(() => structuredClone(treeData), [treeData]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];
    const relationshipUpdates: RelationshipUpdate[] = [];

    // Process each task to detect section changes
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }

        // Find which section this task is now in
        const newSectionItem = updatedItems.find((section) =>
          section.children?.includes(item.id),
        );
        const oldSectionItem = Object.values(oldTreeData).find((section) =>
          section.children?.includes(item.id),
        );

        if (!newSectionItem || !oldSectionItem) {
          return;
        }

        const newSectionId = newSectionItem.id;
        const oldSectionId = oldSectionItem.id;

        // Skip if task hasn't moved between sections
        if (newSectionId === oldSectionId) {
          return;
        }

        const taskPrefixedId = createPrefixId(item.id, IDPrefixes.Task);

        // Get current tag relationships for this task to preserve other tags
        const relatedIds = relationshipsMap[taskPrefixedId] ?? [];
        const currentTagIds = relatedIds
          .filter((id) => getPrefixedIdPrefix(id) === IDPrefixes.Tag)
          .map((id) => getPrefixedId(id));

        // Remove old tag relationship (only if it exists and not untagged)
        if (
          oldSectionId !== untaggedId &&
          currentTagIds.includes(oldSectionId)
        ) {
          const oldTagPrefixedId = createPrefixId(oldSectionId, IDPrefixes.Tag);
          relationshipUpdates.push({
            id: oldTagPrefixedId,
            remove: [taskPrefixedId],
          });
        }

        // Add new tag relationship (only if it doesn't already exist and not untagged)
        if (
          newSectionId !== untaggedId &&
          !currentTagIds.includes(newSectionId)
        ) {
          const newTagPrefixedId = createPrefixId(newSectionId, IDPrefixes.Tag);
          relationshipUpdates.push({
            id: newTagPrefixedId,
            add: [taskPrefixedId],
          });
        }
      }

      // Collect section updates for custom order
      if (item.type === TreeItemTypes.Tag) {
        updatedSections.push({
          id: item.id,
          taskSorting: item.children ?? [],
        });
      }
    });

    // Pass relationship updates to onReorder callback
    onReorder(
      updatedSections,
      [],
      relationshipUpdates.length ? relationshipUpdates : undefined,
    );
  };

  const renderItem: React.FC<RenderItemProps> = ({
    item,
    itemInstance,
    tree,
  }) => {
    const type = item.type;

    // Task
    if (type === TreeItemTypes.Task) {
      return tasksMap[item.sourceId] ? (
        <TreeTaskCard
          task={tasksMap[item.sourceId]}
          tree={tree}
          item={itemInstance}
          tasks={tasks}
        />
      ) : null;
    }

    // Tag
    if (type === TreeItemTypes.Tag) {
      return <TagHeader item={itemInstance} tags={tags} />;
    }

    return null;
  };

  return (
    <VirtualizedTreeList
      rootItemId={rootId}
      items={treeData}
      renderItem={renderItem}
      scrollElement={scrollElement}
      multiselectTypes={taskTreeItemTypes}
      onReorder={_onReorder}
      collapsedItems={collapsedSections}
      onCollapse={onSectionCollapse}
    />
  );
};
